import React from 'react';
import { useField } from 'informed';

import { PhoneNumberField } from '../../PhoneNumberField';

import { FieldContainer } from './styledComponents';

export interface InformedPhoneNumberFieldProps {
  label: string;
  field: string;
  error: string;
  touched: boolean;
  isDisabled: boolean;
  validationSchemaObject: any;
  isHidden?: boolean;
  fieldWrapperStyles?: any;
  wrapperWithPopOverStyles?: string;
  popOverStyle?: string;
  intlPhoneNumberFieldStyles?: string;
  fieldContainerStyles?: string;
  allowedCountriesList?: string[];
  initialCountryCode?: string;
  phoneNumberInputStyles?: string;
  intlPhoneNumberContainerStyles?: string;
  listBoxStyles?: string;
  popOverStyles?: string;
  initialMobileNumber?: string;
  values?: any;
  t?: any;
  placeholder?: string;
  reRenderedFieldsObjects?: any;
  helperText?: string;
  autoFocus?: boolean;
  helperTextStyles?: string;
  fieldValidations?: any;
  countryCodeField?: string;
  dialCodeField?: string;
  validateOnBlur?: boolean;
  labelStyles?: string;
  inputFieldStyles?: string;
  errorMessageStyles?: string;
  isMarginBottomZero?: boolean;
  handleFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
  phoneFieldContainerClassName?: string;
  phoneFieldCountryBtnClassName?: string;
  phoneFieldCountryTextClassName?: string;
  phoneFieldDropdownIconClassName?: string;
  phoneFieldDropdownClassName?: string;
  phoneFieldDropdownListClassName?: string;
  phoneFieldOptionBtnClassName?: string;
  phoneFieldCountryNameClassName?: string;
  phoneFieldDialCodeClassName?: string;
  phoneFieldCheckmarkClassName?: string;
  phoneFieldErrorTextClassName?: string;
  phoneFieldHelperTextClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  // Right element/button support (inside input field)
  rightElement?: React.ReactNode;
  rightButtonText?: string;
  rightButtonAction?: () => void;
  // External button support (outside input container)
  externalButtonText?: string;
  externalButtonAction?: () => void;
  externalButtonClassName?: string;
  // External button element - allows passing entire button JSX (e.g., OTP buttons with conditional rendering)
  externalButtonElement?: React.ReactNode;
}

const InformedPhoneNumberField = (props: InformedPhoneNumberFieldProps) => {
  const {
    label,
    field,
    error,
    touched,
    isDisabled,
    isHidden,
    fieldWrapperStyles,
    wrapperWithPopOverStyles,
    intlPhoneNumberFieldStyles,
    fieldContainerStyles,
    allowedCountriesList,
    initialCountryCode,
    phoneNumberInputStyles,
    intlPhoneNumberContainerStyles,
    listBoxStyles,
    popOverStyles,
    initialMobileNumber,
    placeholder,
    helperText,
    validationSchemaObject,
    helperTextStyles,
    autoFocus,
    fieldValidations = {},
    countryCodeField = 'countryCode',
    dialCodeField = 'dialCode',
    labelStyles,
    validateOnBlur = true,
    inputFieldStyles,
    errorMessageStyles,
    isMarginBottomZero,
    handleFocus,
    handleBlur,
    className,
    phoneFieldContainerClassName,
    phoneFieldCountryBtnClassName,
    phoneFieldCountryTextClassName,
    phoneFieldDropdownIconClassName,
    phoneFieldDropdownClassName,
    phoneFieldDropdownListClassName,
    phoneFieldOptionBtnClassName,
    phoneFieldCountryNameClassName,
    phoneFieldDialCodeClassName,
    phoneFieldCheckmarkClassName,
    phoneFieldErrorTextClassName,
    phoneFieldHelperTextClassName,
    labelClassName,
    inputClassName,
    rightElement,
    rightButtonText,
    rightButtonAction,
    externalButtonText,
    externalButtonAction,
    externalButtonClassName,
    externalButtonElement
  } = props;

  // Dial code field
  const {
    fieldApi: dialCodeFieldApi,
    fieldState: dialCodeFieldState,
    informed: dialCodeInformed
  } = useField({
    field: dialCodeField,
    validateOnBlur: true,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[dialCodeField],
    error
  });

  // Country code field
  const {
    fieldApi: countryCodeFieldApi,
    fieldState: countryCodeFieldState,
    informed: countryCodeInformed
  } = useField({
    field: countryCodeField,
    validateOnBlur: true,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[countryCodeField],
    error
  });

  // Main phone number field
  const { fieldState, render, informed, fieldApi } = useField({
    field,
    validateOnBlur: validateOnBlur,
    validateOnChange: true,
    // @ts-ignore
    validationSchema: validationSchemaObject.fields[field],
    error,
    isDisabled,
    label,
    dialCode: dialCodeFieldState.value,
    countryCode: countryCodeFieldState.value,
    ...fieldValidations
  });

  const { value: phoneNumber } = fieldState;
  const { value: countryCode } = countryCodeFieldState;
  const { value: dialCode } = dialCodeFieldState;

  const { onBlur, onChange } = informed;
  const { onChange: dialCodeOnChange } = dialCodeInformed;
  const { onChange: countryCodeOnChange } = countryCodeInformed;
  const { setValue } = fieldApi;
  const { setValue: setDialCode } = dialCodeFieldApi;
  const { setValue: setCountryCode } = countryCodeFieldApi;

  // Phone number formatting logic (same as Omega)
  const formatPhoneNumber = (phoneNumber: string) => {
    const countryCodePattern = /^(?:\+91|0)/;
    const hasCountryCode = countryCodePattern.test(phoneNumber);
    let cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
    if (
      cleanedPhoneNumber &&
      hasCountryCode &&
      cleanedPhoneNumber.length > 10
    ) {
      cleanedPhoneNumber = cleanedPhoneNumber.slice(-10);
    }
    setValue(cleanedPhoneNumber);
    onChange(cleanedPhoneNumber);
  };

  const handlePhoneNumberChange = (value: string) => {
    formatPhoneNumber(value);
  };

  const handleCountryCodeChange = (code: string) => {
    setCountryCode(code);
    countryCodeOnChange(code);
  };

  const handleDialCodeChange = (dialCode: string) => {
    setDialCode(dialCode);
    dialCodeOnChange(dialCode);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur(e);
    if (handleBlur) {
      handleBlur(e);
    }
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (handleFocus) {
      handleFocus(e);
    }
  };

  const renderPhoneNumberField = () => {
    return (
      <PhoneNumberField
        name={field}
        label={label}
        phoneNumber={phoneNumber as string}
        countryCode={countryCode as string}
        dialCode={dialCode as string}
        onChangePhoneNumber={handlePhoneNumberChange}
        onChangeCountryCode={handleCountryCodeChange}
        onChangeDialCode={handleDialCodeChange}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        error={error}
        helperText={helperText}
        isDisabled={isDisabled}
        isRequired={false}
        allowedCountries={allowedCountriesList}
        placeholder={placeholder}
        className={intlPhoneNumberFieldStyles || className}
        labelClassName={labelStyles || labelClassName}
        inputClassName={
          phoneNumberInputStyles || inputFieldStyles || inputClassName
        }
        phoneFieldContainerClassName={
          intlPhoneNumberContainerStyles || phoneFieldContainerClassName
        }
        phoneFieldCountryBtnClassName={phoneFieldCountryBtnClassName}
        phoneFieldCountryTextClassName={phoneFieldCountryTextClassName}
        phoneFieldDropdownIconClassName={phoneFieldDropdownIconClassName}
        phoneFieldDropdownClassName={phoneFieldDropdownClassName}
        phoneFieldDropdownListClassName={phoneFieldDropdownListClassName}
        phoneFieldOptionBtnClassName={phoneFieldOptionBtnClassName}
        phoneFieldCountryNameClassName={phoneFieldCountryNameClassName}
        phoneFieldDialCodeClassName={phoneFieldDialCodeClassName}
        phoneFieldCheckmarkClassName={phoneFieldCheckmarkClassName}
        phoneFieldErrorTextClassName={phoneFieldErrorTextClassName}
        phoneFieldHelperTextClassName={phoneFieldHelperTextClassName}
        rightElement={rightElement}
        rightButtonText={rightButtonText}
        rightButtonAction={rightButtonAction}
        externalButtonText={externalButtonText}
        externalButtonAction={externalButtonAction}
        externalButtonClassName={externalButtonClassName}
        externalButtonElement={externalButtonElement}
      />
    );
  };

  return render(
    <FieldContainer
      key={field}
      isHidden={isHidden}
      css={fieldWrapperStyles}
      isMarginBottomZero={isMarginBottomZero}
    >
      {renderPhoneNumberField()}
    </FieldContainer>
  );
};

InformedPhoneNumberField.displayName = 'InformedPhoneNumberField';

export { InformedPhoneNumberField };
